import { useEffect, useState, type FormEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { isAxiosError } from 'axios';
import { Plus } from 'lucide-react';
import { apiClient } from '@/lib/apiClient';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Card, CardHeader, CardTitle } from '@/components/ui/Card';

interface CampaignSummary {
  id: string;
  name: string;
}

interface CaptureFormSummary {
  id: string;
  campaignId: string;
  slug: string;
  publicToken: string;
  title: string;
  status: string;
}

const STATUS_CLASSES: Record<string, string> = {
  ACTIVE: 'bg-green-900/40 text-green-200',
  PAUSED: 'bg-amber-900/40 text-amber-200',
  ARCHIVED: 'bg-muted text-muted-foreground',
};

/**
 * Story 48.4b — admin list / create page for lead-capture forms.
 *
 * Edit lives at /admin/lead-capture-forms/:id (separate page).
 */
export function LeadCaptureFormsPage() {
  const [forms, setForms] = useState<CaptureFormSummary[]>([]);
  const [campaigns, setCampaigns] = useState<CampaignSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [refresh, setRefresh] = useState(0);
  const [searchParams, setSearchParams] = useSearchParams();
  const newForCampaign = searchParams.get('newForCampaign');

  // When the campaign-create flow redirects here with ?newForCampaign=<id>,
  // auto-open the create dialog with that campaign pre-selected. Strip the
  // query param once consumed so reloads don't keep re-triggering.
  useEffect(() => {
    if (newForCampaign && !loading) {
      setCreateOpen(true);
      const next = new URLSearchParams(searchParams);
      next.delete('newForCampaign');
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newForCampaign, loading]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([
      apiClient.get<CaptureFormSummary[]>('/lead-capture-forms'),
      apiClient.get<CampaignSummary[]>('/campaigns'),
    ])
      .then(([f, c]) => {
        if (cancelled) return;
        setForms(f.data);
        setCampaigns(c.data);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [refresh]);

  if (loading) {
    return <p className="p-6 text-sm text-muted-foreground">Loading…</p>;
  }

  return (
    <div className="space-y-4 p-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">Lead capture forms</h1>
        <Button size="sm" onClick={() => setCreateOpen(true)}>
          <Plus className="mr-1 h-4 w-4" /> New form
        </Button>
      </div>

      <p className="text-sm text-muted-foreground">
        Public forms hosted at <code>/forms/&lt;token&gt;</code>. Drop the
        URL into ad creatives on Instagram, Facebook, X, LinkedIn or
        TikTok. Submissions create leads in the linked campaign with
        attribution captured automatically.
      </p>

      {forms.length === 0 && (
        <Card>
          <p className="text-sm text-muted-foreground">
            No forms yet. Create one to start capturing leads from external
            campaigns.
          </p>
        </Card>
      )}

      {forms.length > 0 && (
        <Card className="p-0 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-4 py-2 text-left">Title</th>
                <th className="px-4 py-2 text-left">Slug</th>
                <th className="px-4 py-2 text-left">Campaign</th>
                <th className="px-4 py-2 text-left">Public URL</th>
                <th className="px-4 py-2 text-left">Status</th>
              </tr>
            </thead>
            <tbody>
              {forms.map((form) => {
                const campaign = campaigns.find((c) => c.id === form.campaignId);
                const publicUrl = `${window.location.origin}/forms/${form.publicToken}`;
                return (
                  <tr key={form.id} className="border-t">
                    <td className="px-4 py-2">
                      <Link
                        to={`/admin/lead-capture-forms/${form.id}`}
                        className="font-medium text-primary hover:underline"
                      >
                        {form.title}
                      </Link>
                    </td>
                    <td className="px-4 py-2 font-mono text-xs">{form.slug}</td>
                    <td className="px-4 py-2 text-muted-foreground">
                      {campaign?.name ?? '—'}
                    </td>
                    <td className="px-4 py-2">
                      <CopyableLink href={publicUrl} />
                    </td>
                    <td className="px-4 py-2">
                      <span
                        className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                          STATUS_CLASSES[form.status] ?? 'bg-muted'
                        }`}
                      >
                        {form.status}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Card>
      )}

      {createOpen && (
        <CreateFormDialog
          campaigns={campaigns}
          defaultCampaignId={newForCampaign ?? undefined}
          onClose={() => setCreateOpen(false)}
          onCreated={() => {
            setCreateOpen(false);
            setRefresh((x) => x + 1);
          }}
        />
      )}
    </div>
  );
}

function CopyableLink({ href }: { href: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      onClick={() => {
        void navigator.clipboard.writeText(href);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      }}
      className="group inline-flex items-center gap-2 rounded-md bg-muted px-2 py-1 text-xs hover:bg-muted/80"
      title="Click to copy"
    >
      <span className="max-w-[18rem] truncate font-mono">{href}</span>
      <span className="text-muted-foreground">{copied ? '✓ copied' : 'copy'}</span>
    </button>
  );
}

function CreateFormDialog({
  campaigns,
  defaultCampaignId,
  onClose,
  onCreated,
}: {
  campaigns: CampaignSummary[];
  defaultCampaignId?: string;
  onClose: () => void;
  onCreated: () => void;
}) {
  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [campaignId, setCampaignId] = useState(
    defaultCampaignId && campaigns.some((c) => c.id === defaultCampaignId)
      ? defaultCampaignId
      : campaigns[0]?.id ?? '',
  );
  const [introText, setIntroText] = useState('');
  const [thankYouText, setThankYouText] = useState('Thank you. We will contact you shortly.');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Default fields covering the common case (firstName/lastName/phone/email/companyName).
  const [fields] = useState([
    { name: 'firstName', label: 'First name', type: 'text', required: true },
    { name: 'lastName', label: 'Last name', type: 'text', required: true },
    { name: 'phone', label: 'Phone / WhatsApp', type: 'phone', required: true },
    { name: 'email', label: 'Email', type: 'email', required: false },
    { name: 'companyName', label: 'Company', type: 'text', required: false },
  ]);

  function autoSlug(t: string): string {
    return t
      .toLowerCase()
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  async function handleCreate(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!campaignId) {
      setError('Pick a campaign');
      return;
    }
    setSubmitting(true);
    try {
      await apiClient.post('/lead-capture-forms', {
        campaignId,
        slug: slug.trim() || autoSlug(title),
        title,
        introText: introText.trim() || null,
        thankYouText: thankYouText.trim() || null,
        fieldConfig: fields,
      });
      onCreated();
    } catch (err) {
      if (isAxiosError(err)) {
        const detail = (err.response?.data as { detail?: string } | undefined)?.detail;
        setError(detail ?? 'Could not create form.');
      } else {
        setError('Could not create form.');
      }
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-lg rounded-lg bg-surface-3 p-6 text-on-surface shadow-lg">
        <CardHeader>
          <CardTitle>New lead capture form</CardTitle>
          <button
            type="button"
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground"
          >
            ✕
          </button>
        </CardHeader>
        <form onSubmit={handleCreate} className="space-y-3">
          <div className="space-y-1">
            <label className="text-xs font-medium">Title</label>
            <Input
              required
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Reserve more with WhatsApp — Lopettia"
            />
          </div>
          <div className="space-y-1">
            <label className="text-xs font-medium">Slug (URL segment)</label>
            <Input
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              placeholder={autoSlug(title) || 'auto-from-title'}
            />
          </div>
          <div className="space-y-1">
            <label className="text-xs font-medium">Campaign</label>
            <select
              required
              value={campaignId}
              onChange={(e) => setCampaignId(e.target.value)}
              className="w-full rounded-md border bg-background px-3 py-2 text-sm"
            >
              {campaigns.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1">
            <label className="text-xs font-medium">Intro text (shown above the form)</label>
            <textarea
              rows={2}
              value={introText}
              onChange={(e) => setIntroText(e.target.value)}
              className="w-full rounded-md border bg-background p-2 text-sm"
              placeholder="Tell us about your restaurant. We will call you today."
            />
          </div>
          <div className="space-y-1">
            <label className="text-xs font-medium">Thank-you message</label>
            <textarea
              rows={2}
              value={thankYouText}
              onChange={(e) => setThankYouText(e.target.value)}
              className="w-full rounded-md border bg-background p-2 text-sm"
            />
          </div>

          <p className="rounded-md bg-muted p-3 text-xs text-muted-foreground">
            Default fields: First name, Last name, Phone/WhatsApp, Email,
            Company. You can edit the field list after creation.
          </p>

          {error && <p className="text-sm text-destructive">{error}</p>}

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" size="sm" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" size="sm" disabled={submitting}>
              {submitting ? 'Creating…' : 'Create form'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
